import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { setTokenGetter, setOrganizationIdGetter, setRefreshFunction, AUTH_TOKEN_KEY } from '@/core/api'
import { usePermissionStore } from '@/stores/permissions'
import { setSentryUser, clearSentryUser } from '@/core/sentry'
import localAuth, {
    type UserProfileDto,
    type LoginDto,
    type SendMagicLinkDto,
    type RequestPasswordResetDto,
    type OrganizationInfo,
} from '@/core/localAuth'

const REFRESH_TOKEN_KEY = 'rws-refresh-token'
const CURRENT_ORGANIZATION_KEY = 'rws_current_organization'
const REFRESH_BUFFER_MS = 60 * 1000 // Refresh 60 seconds before expiry

/**
 * Auth store - manages user identity, roles, and RBAC
 * Uses local JWT authentication with multi-organization membership support
 */
export const useAuthStore = defineStore('auth', () => {
    // State
    const accessToken = ref<string | null>(localStorage.getItem(AUTH_TOKEN_KEY))
    const refreshToken = ref<string | null>(localStorage.getItem(REFRESH_TOKEN_KEY))
    const user = ref<UserProfileDto | null>(null)
    const organizations = ref<OrganizationInfo[]>([])
    // Store current organization directly (for Super Admins accessing non-member organizations)
    const currentOrganizationInfo = ref<OrganizationInfo | null>(
        JSON.parse(localStorage.getItem(CURRENT_ORGANIZATION_KEY) || 'null')
    )
    const isInitialized = ref(false)
    const isLoading = ref(false)
    const tokenExpiresAt = ref<number | null>(
        localStorage.getItem('rws_token_expires_at')
            ? parseInt(localStorage.getItem('rws_token_expires_at')!, 10)
            : null
    )
    let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null

    // Register getters with API service immediately
    setTokenGetter(() => accessToken.value)
    setOrganizationIdGetter(() => currentOrganizationInfo.value?.id || user.value?.organizationId)
    setRefreshFunction(refreshAccessToken)

    // Getters
    const isAuthenticated = computed(() => !!accessToken.value && !!user.value)

    /** Whether the current user is a Super Admin */
    const isSuperAdmin = computed(() => !!user.value?.isSuperAdmin)
    const hasOrganization = computed(() => !!currentOrganizationInfo.value?.id || !!user.value?.organizationId)
    const currentOrganizationId = computed(() => currentOrganizationInfo.value?.id || user.value?.organizationId)

    /** Current organization info */
    const currentOrganization = computed(() => {
        // First priority: directly stored organization info (from selectOrganization)
        if (currentOrganizationInfo.value) return currentOrganizationInfo.value

        // Fallback: find in organizations array
        const org = organizations.value.find(o => o.id === user.value?.organizationId)
        if (org) return org

        // Last fallback: find in user memberships
        const membership = user.value?.memberships?.find(m => m.organizationId === user.value?.organizationId)
        if (membership) {
            return {
                id: membership.organizationId,
                name: membership.organizationName,
                roleIds: membership.assignedRoleIds,
                isDefault: membership.isDefault,
            }
        }

        return undefined
    })

    /** Whether user has multiple organizations */
    const hasMultipleOrganizations = computed(() => organizations.value.length > 1)

    const displayName = computed(() => {
        if (!user.value) return ''
        if (user.value.firstName || user.value.lastName) {
            return `${user.value.firstName || ''} ${user.value.lastName || ''}`.trim()
        }
        return user.value.email
    })

    // Actions

    /**
     * Initialize the auth store on app load
     */
    async function initialize() {
        // If we have a token, try to restore the session
        if (accessToken.value) {
            // Proactively refresh the access token if it's expired or about to expire
            const now = Date.now()
            const isTokenExpired = tokenExpiresAt.value && tokenExpiresAt.value - now < REFRESH_BUFFER_MS

            if (isTokenExpired && refreshToken.value) {
                console.log('Access token expired on reload, refreshing proactively…')
                const newToken = await refreshAccessToken()
                if (!newToken) {
                    // Refresh failed — session is truly expired
                    isInitialized.value = true
                    return
                }
            }

            await fetchUserProfile()

            // Load RBAC permissions
            const permissionStore = usePermissionStore()
            await permissionStore.load()

            // Set up refresh if we have an expiry
            if (tokenExpiresAt.value) {
                scheduleTokenRefresh()
            }
        }

        isInitialized.value = true
    }

    /**
     * Login with email and password
     */
    async function login(credentials: LoginDto): Promise<boolean> {
        isLoading.value = true
        try {
            const response = await localAuth.login(credentials)

            // Store tokens
            setAccessToken(response.access_token)
            setRefreshToken(response.refresh_token)

            // Store organization list
            organizations.value = response.organizations || []

            // Calculate expiry time
            tokenExpiresAt.value = Date.now() + (response.expires_in * 1000)
            localStorage.setItem('rws_token_expires_at', tokenExpiresAt.value.toString())

            // Fetch user profile
            const profileSuccess = await fetchUserProfile()
            if (!profileSuccess) return false

            // Load RBAC permissions
            const permissionStore = usePermissionStore()
            await permissionStore.load()

            // Schedule refresh
            scheduleTokenRefresh()

            return true
        } catch (error) {
            console.error('Login failed:', error)
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Logout and clear all auth state
     */
    async function logout() {
        // Revoke refresh token on server
        if (refreshToken.value) {
            try {
                await localAuth.logoutApi(refreshToken.value)
            } catch (error) {
                console.warn('Failed to revoke refresh token:', error)
            }
        }

        clearAuth()

        // Force a full page reload to the login page to ensure all state is reset
        window.location.href = '/login'
    }

    /**
     * Switch to a different organization
     */
    async function selectOrganization(organizationId: string): Promise<boolean> {
        isLoading.value = true
        try {
            const response = await localAuth.selectOrganization(organizationId)

            // Update tokens
            setAccessToken(response.access_token)
            tokenExpiresAt.value = Date.now() + (response.expires_in * 1000)
            localStorage.setItem('rws_token_expires_at', tokenExpiresAt.value.toString())

            // Store current organization directly from response
            currentOrganizationInfo.value = response.organization || null
            if (response.organization) {
                localStorage.setItem(CURRENT_ORGANIZATION_KEY, JSON.stringify(response.organization))
            }

            // Update Sentry organization tag
            setSentryUser(user.value, response.organization?.id || organizationId)

            // Fetch new user profile for the organization context
            await fetchUserProfile()

            // Reload RBAC permissions for new org context
            const permissionStore = usePermissionStore()
            await permissionStore.load()

            // Reschedule refresh
            scheduleTokenRefresh()

            return true
        } catch (error) {
            console.error('Failed to switch organization:', error)
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Refresh the access token.
     * Returns the new token on success, or null on failure.
     * On failure, clears auth state. The api.ts interceptor handles
     * redirecting to /login so there's no race with window.location.href.
     */
    async function refreshAccessToken(): Promise<string | null> {
        if (!refreshToken.value) {
            console.warn('No refresh token available')
            clearAuth()
            return null
        }

        try {
            const response = await localAuth.refreshAccessToken(refreshToken.value)

            setAccessToken(response.access_token)
            tokenExpiresAt.value = Date.now() + (response.expires_in * 1000)
            localStorage.setItem('rws_token_expires_at', tokenExpiresAt.value.toString())

            // If the user is viewing a non-default organization (common for super admins),
            // re-select it so the new JWT is scoped to the correct tenant.
            // Without this, the refreshed JWT targets the user's default org and API calls
            // intermittently return data for the wrong organization.
            const activeOrgId = currentOrganizationInfo.value?.id
            const defaultOrgId = user.value?.organizationId
            if (activeOrgId && activeOrgId !== defaultOrgId) {
                try {
                    const orgResponse = await localAuth.selectOrganization(activeOrgId)
                    setAccessToken(orgResponse.access_token)
                    tokenExpiresAt.value = Date.now() + (orgResponse.expires_in * 1000)
                    localStorage.setItem('rws_token_expires_at', tokenExpiresAt.value.toString())
                    currentOrganizationInfo.value = orgResponse.organization
                    localStorage.setItem(CURRENT_ORGANIZATION_KEY, JSON.stringify(orgResponse.organization))
                } catch (orgError) {
                    console.warn('Failed to re-select organization after token refresh:', orgError)
                    // Continue with the refreshed token — the X-Organization-ID header
                    // will still be sent, so most requests will still work.
                }
            }

            // Reload RBAC permissions with the refreshed token
            const permissionStore = usePermissionStore()
            await permissionStore.load()

            // Schedule next refresh
            scheduleTokenRefresh()

            return accessToken.value
        } catch (error) {
            console.error('Failed to refresh token:', error)
            clearAuth()
            return null
        }
    }

    /**
     * Schedule token refresh before expiry
     */
    function scheduleTokenRefresh() {
        // Clear existing timeout
        if (refreshTimeoutId) {
            clearTimeout(refreshTimeoutId)
            refreshTimeoutId = null
        }

        if (!tokenExpiresAt.value) return

        const now = Date.now()
        const timeUntilRefresh = tokenExpiresAt.value - now - REFRESH_BUFFER_MS

        if (timeUntilRefresh <= 0) {
            // Token is about to expire or already expired, refresh immediately
            refreshAccessToken()
        } else {
            refreshTimeoutId = setTimeout(() => {
                refreshAccessToken()
            }, timeUntilRefresh)
        }
    }

    /**
     * Send magic link email for passwordless login
     */
    async function sendMagicLink(data: SendMagicLinkDto): Promise<boolean> {
        isLoading.value = true
        try {
            await localAuth.sendMagicLink(data)
            return true
        } catch (error) {
            console.error('Failed to send magic link:', error)
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Handle magic link callback - verify token and complete login flow
     */
    async function handleMagicLinkCallback(token: string): Promise<boolean> {
        isLoading.value = true
        try {
            // Clear any stale auth state so the API interceptor
            // doesn't attach an old/expired Bearer token
            clearAuth()

            const response = await localAuth.verifyMagicLink(token)

            // Store tokens
            setAccessToken(response.access_token)
            setRefreshToken(response.refresh_token)

            // Store organization list
            organizations.value = response.organizations || []

            // Calculate expiry time
            tokenExpiresAt.value = Date.now() + (response.expires_in * 1000)
            localStorage.setItem('rws_token_expires_at', tokenExpiresAt.value.toString())

            // Fetch user profile
            const profileSuccess = await fetchUserProfile()
            if (!profileSuccess) return false

            // Load RBAC permissions
            const permissionStore = usePermissionStore()
            await permissionStore.load()

            // Schedule refresh
            scheduleTokenRefresh()

            return true
        } catch (error) {
            console.error('Magic link verification failed:', error)
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Request password reset email
     */
    async function requestPasswordReset(data: RequestPasswordResetDto): Promise<boolean> {
        isLoading.value = true
        try {
            await localAuth.requestPasswordReset(data)
            return true
        } catch (error) {
            console.error('Failed to request password reset:', error)
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Fetch user profile from backend /auth/me endpoint
     */
    async function fetchUserProfile(): Promise<boolean> {
        try {
            const profile = await localAuth.getCurrentUserProfile()
            if (profile) {
                user.value = profile

                // Sync user & organization with Sentry
                setSentryUser(profile, currentOrganizationInfo.value?.id || profile.organizationId)

                // Update organizations from memberships if available
                if (profile.memberships && profile.memberships.length > 0) {
                    organizations.value = profile.memberships.map(m => ({
                        id: m.organizationId,
                        name: m.organizationName,
                        roleIds: m.assignedRoleIds,
                        isDefault: m.isDefault,
                    }))
                }

                // Auto-detect and save timezone if not already stored
                if (!profile.timezone) {
                    syncBrowserTimezone()
                }

                return true
            } else {
                console.warn('User not found in backend database')
                clearAuth()
                return false
            }
        } catch (error) {
            console.error('Failed to fetch user profile:', error)
            clearAuth()
            return false
        }
    }

    /**
     * Detect the browser timezone and PATCH it to the user's own profile.
     * Fire-and-forget — failures are logged but never block the user.
     */
    async function syncBrowserTimezone() {
        try {
            const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone
            if (!detectedTz) return

            const { default: api } = await import('@/core/api')
            const response = await api.patch<{ ok: boolean; data: UserProfileDto }>('/auth/me', {
                timezone: detectedTz,
            })

            // Update local user state with the saved timezone
            if (user.value && response.data?.data) {
                user.value = { ...user.value, timezone: response.data.data.timezone }
            }
        } catch (err) {
            console.warn('Failed to save browser timezone:', err)
        }
    }

    /**
     * Refresh organizations list (for when new organizations are added)
     * Calls fetchUserProfile to update the organizations from user memberships
     */
    async function refreshOrganizations(): Promise<void> {
        await fetchUserProfile()
    }

    /**
     * Set access token in state and local storage
     */
    function setAccessToken(token: string) {
        accessToken.value = token
        localStorage.setItem(AUTH_TOKEN_KEY, token)
    }

    /**
     * Set refresh token in state and local storage
     */
    function setRefreshToken(token: string) {
        refreshToken.value = token
        localStorage.setItem(REFRESH_TOKEN_KEY, token)
    }


    /**
     * Clear all auth state
     */
    function clearAuth() {
        // Clear refresh timeout
        if (refreshTimeoutId) {
            clearTimeout(refreshTimeoutId)
            refreshTimeoutId = null
        }

        accessToken.value = null
        refreshToken.value = null
        user.value = null
        organizations.value = []
        currentOrganizationInfo.value = null
        tokenExpiresAt.value = null

        localStorage.removeItem(AUTH_TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        localStorage.removeItem(CURRENT_ORGANIZATION_KEY)
        localStorage.removeItem('rws_token_expires_at')

        // Clear RBAC permissions
        const permissionStore = usePermissionStore()
        permissionStore.clear()

        // Clear Sentry user context
        clearSentryUser()
    }

    return {
        // State
        accessToken,
        refreshToken,
        user,
        organizations,
        isInitialized,
        isLoading,
        tokenExpiresAt,
        currentOrganizationInfo,
        // Getters
        isAuthenticated,
        isSuperAdmin,
        hasOrganization,
        currentOrganizationId,
        currentOrganization,
        hasMultipleOrganizations,
        displayName,
        // Actions
        initialize,
        login,
        logout,
        selectOrganization,
        refreshAccessToken,
        sendMagicLink,
        handleMagicLinkCallback,
        requestPasswordReset,
        fetchUserProfile,
        setAccessToken,
        setRefreshToken,
        clearAuth,
        refreshOrganizations,
    }
})

// Preserve auth state during Vite HMR (dev-only)
// Ref: https://pinia.vuejs.org/cookbook/hot-module-replacement.html
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
